import { useWizard } from 'react-use-wizard';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Compass, Wallet, TrendingUp, Shield } from 'lucide-react';

import { Card } from '@design-system';
import { SessionType } from '@shared';
import { selectSession, selectFeatureFlags } from '@client/model/selectors';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useCareHasStarted } from '@/shared/hooks/use-care-has-started';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

const STEPS = [
  {
    icon: <Wallet className="text-primary size-5" />,
    title: 'Identify your assets, income, and existing policies',
    description:
      'Tell us what assets or insurance policies you could use to fund long-term care.',
  },
  {
    icon: <TrendingUp className="text-primary size-5" />,
    title: 'Project Growth & Taxes',
    description: 'See how your assets are expected to grow over time, accounting for taxes.',
  },
  {
    icon: <Shield className="text-primary size-5" />,
    title: 'Explore Insurance Options',
    description: 'Discover insurance products that could fit into your overall care plan.',
  },
];

const WITHOUT_POLICY_STEPS = [
  STEPS[0],
  STEPS[1],
  {
    icon: <Shield className="text-primary size-5" />,
    title: 'Evaluate Preparedness',
    description: 'Compare the growth of your assets to your modeled future LTC event.',
  },
];

export function FundingPlanOverviewStep() {
  const { nextStep } = useWizard();
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const isEdjPilot = useIsEdjPilotAdvisor();
  const careHasStarted = useCareHasStarted();
  const skipPolicySteps = isEdjPilot || careHasStarted;

  const handleBack = () => {
    const destination = isD2C
      ? `/clients/${clientId}/onboarding/care-phases-summary`
      : `/clients/${clientId}/onboarding/funding-sources`;
    navigate(destination);
  };

  return (
    <StepLayout
      title="Step-by-Step Funding Plan"
      subtitle="Discover how your assets, income, or insurance can work together to cover the costs of your LTC plan."
      icon={
        <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
          <Compass className="text-primary size-7" />
        </div>
      }
      nextButtonLabel="Let's Get Started"
      onNext={nextStep}
      onBack={handleBack}
    >
      <div className="flex flex-col items-center">
        <Card className="w-full max-w-3xl p-6">
          <h3 className="text-base font-semibold text-gray-950">Here&apos;s what we&apos;ll do:</h3>
          <div className="mt-4 space-y-5">
            {(skipPolicySteps ? WITHOUT_POLICY_STEPS : STEPS).map(step => (
              <div
                key={step.title}
                className="flex items-start gap-4"
              >
                <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
                  {step.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-950">{step.title}</h4>
                  <p className="text-foreground mt-0.5 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </StepLayout>
  );
}
